import { useCallback, useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import TurnstileWidget from '@/components/marketing/TurnstileWidget'
import QuestionField from '@/components/marketing/technology/enquiry/QuestionField'
import {
  clearDraft, firstStepWithError, isVisible, loadDraft, saveDraft, stepErrors,
  type Answers, type EnquiryPayload,
} from '@/components/marketing/technology/enquiry/enquiryModel'
import { csrfToken, refreshCsrfToken } from '@/lib/csrf'
import { getAttribution } from '@/lib/attribution'
import { track } from '@/lib/analytics'

interface Props {
  brand: { name: string }
  enquiry: EnquiryPayload
}

/* The enterprise enquiry form (spec 2026-10-18). Five steps; the questions,
 * their order and their branches come from EnterpriseEnquiries::Questions via
 * props — never written here. The draft lives in localStorage (no files) so a
 * refresh does not cost ten minutes. */
export default function Enquire({ brand, enquiry }: Props) {
  const { props: shared } = usePage<{ layout?: { turnstile?: { site_key?: string | null } } }>()
  const siteKey = shared.layout?.turnstile?.site_key ?? null
  const steps = enquiry.steps
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Answers>({})
  const [files, setFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [consent, setConsent] = useState(false)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [honeypot, setHoneypot] = useState('')
  const turnstileToken = useRef<string | null>(null)
  const [widgetKey, setWidgetKey] = useState(0)
  const topRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const stepMoved = useRef(false)
  const onVerify = useCallback((t: string) => { turnstileToken.current = t }, [])
  const onExpire = useCallback(() => { turnstileToken.current = null }, [])

  // A Turnstile token is single-use: after any refusal it is spent, so drop it
  // and remount the widget for a fresh challenge.
  function spendToken() {
    turnstileToken.current = null
    setWidgetKey(k => k + 1)
  }

  useEffect(() => {
    if (stepMoved.current) headingRef.current?.focus()
    stepMoved.current = true
  }, [step])

  // A footer entry (?type=distributor) seeds the business type, unless a saved
  // draft already has one: that is what the person chose last time.
  const presetType = enquiry.preset?.business_type
  useEffect(() => {
    const draft = loadDraft()
    if (draft) {
      setAnswers(draft.answers.business_type || !presetType ? draft.answers : { ...draft.answers, business_type: presetType })
      setStep(Math.min(draft.step, steps.length - 1))
    } else if (presetType) {
      setAnswers({ business_type: presetType })
    }
  }, [steps.length, presetType])

  useEffect(() => {
    if (!sent) saveDraft({ step, answers })
  }, [step, answers, sent])

  const last = step === steps.length - 1
  const current = steps[step]

  function change(key: string, value: unknown) {
    setAnswers(a => ({ ...a, [key]: value }))
    setErrors(({ [key]: _gone, ...rest }) => rest)
  }

  function go(to: number) {
    setStep(to)
    topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  function next() {
    const found = stepErrors(current, answers)
    setErrors(found)
    if (Object.keys(found).length === 0) go(step + 1)
  }

  async function submit() {
    const found = stepErrors(current, answers)
    if (!consent) found.consent = 'Please agree before sending.'
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setSending(true)
    const { first_touch, last_touch } = getAttribution()
    const body = () => {
      const form = new FormData()
      form.append('answers', JSON.stringify(answers))
      form.append('attribution', JSON.stringify({
        first_touch, last_touch, referrer: document.referrer,
        ...(enquiry.preset?.entry ? { entry: enquiry.preset.entry } : {}),
      }))
      form.append('consent', '1')
      form.append('company_fax', honeypot)
      if (turnstileToken.current) form.append('cf-turnstile-response', turnstileToken.current)
      files.forEach(f => form.append('royalty_reports[]', f))
      return form
    }
    const send = (token: string) => fetch('/technology/enquire', { method: 'POST', headers: { 'X-CSRF-Token': token }, body: body() })

    try {
      let res = await send(csrfToken())
      let data = await res.json()
      if (res.status === 422 && data.csrf_expired) {
        const fresh = await refreshCsrfToken()
        if (fresh) {
          res = await send(fresh)
          data = await res.json()
        }
      }
      if (res.ok) {
        clearDraft()
        track('generate_lead', { data: { form: 'technology_enquiry', business_type: answers.business_type } })
        setSent(true)
        topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
        return
      }
      spendToken()
      const serverErrors: Record<string, string> = data.errors ?? { base: 'Something went wrong. Please try again.' }
      setErrors(serverErrors)
      const at = firstStepWithError(steps, serverErrors)
      if (at >= 0) go(at)
    } catch {
      spendToken()
      setErrors({ base: `We could not send this. Please try again, or write to ${enquiry.contact_email}.` })
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="container mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-6" ref={topRef}>
      <p className="marketing-eyebrow-mono">{brand.name}</p>
      <h1 className="marketing-section-title mt-3 text-white" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.6rem)' }}>
        Tell us what you run
      </h1>

      {sent ? (
        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-8" role="status">
          <Check className="h-6 w-6 text-[#ff8c4d]" aria-hidden="true" />
          <p className="mt-4 text-white/80">{enquiry.thank_you}</p>
        </div>
      ) : (
        <>
          <p className="marketing-lead mt-5">{enquiry.intro}</p>

          <ol className="mt-10 flex gap-2" aria-label="Progress">
            {steps.map((s, i) => (
              <li key={s.key} className="flex-1">
                <div className={`h-1 rounded-full ${i <= step ? 'bg-[#ff6a1a]' : 'bg-white/10'}`} />
                <span className={`mt-2 hidden text-[11px] sm:block ${i === step ? 'text-white' : 'text-white/40'}`}>
                  {s.title}
                </span>
                {i === step && <span className="sr-only" aria-current="step">Current step: {s.title}</span>}
              </li>
            ))}
          </ol>

          <p className="sr-only" role="status">Step {step + 1} of {steps.length}: {current.title}</p>

          <h2 ref={headingRef} tabIndex={-1} className="mt-8 text-lg font-semibold text-white focus:outline-none">
            {step + 1}. {current.title}
          </h2>

          <div className="mt-6 space-y-8">
            {current.questions.filter(q => isVisible(q, answers)).map(q => (
              <QuestionField key={q.key} q={q} answers={answers} payload={enquiry} error={errors[q.key]} files={files}
                onChange={change} onFiles={setFiles} />
            ))}
          </div>

          {/* Honeypot — invisible to people, filled by bots. */}
          <input type="text" name="company_fax" tabIndex={-1} autoComplete="off" aria-hidden="true"
            className="absolute left-[-9999px] h-px w-px opacity-0" value={honeypot} onChange={e => setHoneypot(e.target.value)} />

          {last && (
            <div className="mt-10 space-y-4">
              <label className="flex items-start gap-3 text-sm text-white/80">
                <input type="checkbox" className="mt-1" checked={consent} onChange={e => setConsent(e.target.checked)} />
                <span>
                  {enquiry.consent_label}{' '}
                  <a href="/privacy-policy" className="underline decoration-white/30 underline-offset-4">Privacy policy</a>
                </span>
              </label>
              {errors.consent && <p role="alert" className="text-xs text-red-400">{errors.consent}</p>}
              {siteKey && (
                <TurnstileWidget key={widgetKey} siteKey={siteKey} action="technology_enquiry"
                  onVerify={onVerify} onExpire={onExpire} />
              )}
              <p className="text-sm text-white/50">{enquiry.closing}</p>
            </div>
          )}

          {errors.base && <p role="alert" className="mt-6 text-sm text-red-400">{errors.base}</p>}

          <div className="mt-10 flex items-center justify-between">
            {step > 0 ? (
              <button type="button" className="marketing-button-secondary" onClick={() => go(step - 1)}>
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : <span />}
            {last ? (
              <button type="button" className="marketing-button-primary" disabled={sending} onClick={submit}>
                {sending ? 'Sending…' : 'Send enquiry'} <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button type="button" className="marketing-button-primary" onClick={next}>
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </>
      )}
    </section>
  )
}
